const PARSED_ROOT_KEYS = ['price', 'netQuantity', 'pricePerUnit', 'vat', 'promotion'];
const PRICE_PER_UNIT_REVIEW_KEYS = ['price', 'netQuantity', 'pricePerUnit'];

function isParsedRecord(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function parsedReviewTarget(attribute) {
  const base = String(attribute || '').replace(/^extract\./, '');
  return base === 'promotionText' ? 'promotion' : base;
}

function isRootedParsedConfig(obj) {
  return isParsedRecord(obj) && PARSED_ROOT_KEYS.some((key) => key in obj);
}

/**
 * Put old and repair parsed configs in the same shape so the line-diff
 * compares the editable closure, not a child object vs a rooted file.
 * For already-stored review payloads that only have the attribute child on
 * the left, missing sibling roots are filled from the other side.
 */
export function alignParsedConfigDiff(attribute, oldConfig, newConfig) {
  const target = parsedReviewTarget(attribute);
  if (target !== 'pricePerUnit') {
    const unwrap = (obj) => (
      isRootedParsedConfig(obj) && isParsedRecord(obj[target]) ? obj[target] : obj
    );
    return { oldConfig: unwrap(oldConfig) || {}, newConfig: unwrap(newConfig) || {} };
  }

  const childOf = (obj) => {
    if (isRootedParsedConfig(obj) && isParsedRecord(obj[target])) return obj[target];
    if (isParsedRecord(obj) && !isRootedParsedConfig(obj)) return obj;
    return undefined;
  };

  const build = (obj, other) => {
    const rooted = isRootedParsedConfig(obj) ? obj : null;
    const view = {};
    for (const key of PRICE_PER_UNIT_REVIEW_KEYS) {
      const value = rooted && key in rooted
        ? rooted[key]
        : key === target
          ? (childOf(obj) ?? childOf(other))
          : (isRootedParsedConfig(other) ? other[key] : undefined);
      if (value !== undefined) view[key] = value;
    }
    return Object.keys(view).length ? view : (obj || {});
  };

  return {
    oldConfig: build(oldConfig, newConfig),
    newConfig: build(newConfig, oldConfig),
  };
}

/** Format a sandbox /parse-value result for sample rows. */
export function formatParsedResult(parsed) {
  if (!parsed) return null;
  if (parsed.error) return { text: parsed.error, ok: false };
  if (!parsed.matched) return { text: 'no match', ok: false };
  const bits = [];
  if (parsed.value != null) bits.push(String(parsed.value));
  if (parsed.unit) bits.push(String(parsed.unit));
  return { text: bits.length ? bits.join(' ') : 'matched', ok: true };
}

/** Join Edit Config validation rows by sample_index. */
export function parsedBySampleIndex(validation) {
  const map = {};
  const results = validation?.results;
  if (!Array.isArray(results)) return map;
  for (const row of results) {
    if (row && row.sample_index != null) map[row.sample_index] = row;
  }
  return map;
}

/**
 * Join review-panel parsed_config_check rows by html_url.
 * Prefers `results` (includes successes); falls back to mismatches.
 */
export function parsedByHtmlUrl(check) {
  const map = {};
  if (!check) return map;
  if (Array.isArray(check.results)) {
    for (const row of check.results) {
      if (row?.html_url != null) map[String(row.html_url)] = row;
    }
  }
  if (Array.isArray(check.mismatches)) {
    for (const row of check.mismatches) {
      const key = row?.html_url != null ? String(row.html_url) : null;
      if (key && map[key] == null) {
        map[key] = { ...row, matched: false };
      }
    }
  }
  return map;
}

/**
 * Resolve the parse outcome for one sample row.
 * Prefer a value already attached on the row, then sample_index, html_url,
 * then extracted text (same raw string always parses the same way).
 */
export function parsedForSample(check, result) {
  if (!result) return null;
  if (result.parsed) return result.parsed;
  if (!check) return null;

  const results = Array.isArray(check.results) ? check.results : [];
  const mismatches = Array.isArray(check.mismatches) ? check.mismatches : [];

  if (result.sample_index != null && result.is_passing_sample !== true) {
    const byIndex = results.find((row) => row && row.sample_index === result.sample_index);
    if (byIndex) return byIndex;
  }

  const url = result.html_url != null ? String(result.html_url) : null;
  if (url) {
    const byUrl = results.find((row) => row?.html_url != null && String(row.html_url) === url);
    if (byUrl) return byUrl;
    const mismatch = mismatches.find((row) => row?.html_url != null && String(row.html_url) === url);
    if (mismatch) return { ...mismatch, matched: false };
  }

  const extracted = result.extracted_value != null ? String(result.extracted_value) : null;
  if (extracted) {
    const byText = results.find((row) => row?.raw_text === extracted);
    if (byText) return byText;
    const mismatch = mismatches.find((row) => row?.raw_text === extracted);
    if (mismatch) return { ...mismatch, matched: false };
  }

  return null;
}
